import {Category, FlatCategory, SourceCategory, TreeCategory} from "../../domain/category"
import {createContext} from "react"
import {getCategoryColors, getColorForTopLevelCategory} from "../../components-shared/colors/colors"

export const CategoryHelperContext = createContext<CategoryHelper | null>(null)

export class CategoryHelper {
  public categories: Category[]
  public categoriesIncludingTopLevel: FlatCategory[]
  public flatSubCategories: FlatCategory[]
  public categoryNamesById: Record<number, string>
  public categoriesById: Record<number, Category>
  public mainColorById: Record<number, string>
  public categoryTree: TreeCategory[]

  constructor(sourceCategories: SourceCategory[]) {
    this.categories = this.getCategoriesWithColor(sourceCategories)

    this.categoriesIncludingTopLevel = this.flattenCategories()
    this.flatSubCategories = this.categoriesIncludingTopLevel.filter(c => c.topLevelCategory !== undefined)
    this.categoryNamesById = this.getCategoryNamesById()
    this.categoriesById = this.getCategoriesById()
    this.addTopLevelCategoryNamesById()
    this.categoryTree = this.createCategoryTree()
    this.mainColorById = this.getMainColorById()
  }

  private getCategoriesWithColor(sourceCategories: SourceCategory[]): Category[] {
    const topLevelSourceCategories = sourceCategories.filter(c => c.parentId === undefined)

    const categories: Category[] = []

    topLevelSourceCategories.forEach(topLevelSourceCategory => {
      const topLevelCategory = topLevelSourceCategory as Category
      topLevelCategory.color = getColorForTopLevelCategory(topLevelCategory.id)
      categories.push(topLevelCategory)

      const subSourceCategories = sourceCategories.filter(c => c.parentId === topLevelCategory.id)
      const subCategoryColors = getCategoryColors(topLevelCategory.id, subSourceCategories.length)
      subSourceCategories.forEach((subSourceCategory, index) => {
        const subCategory = subSourceCategory as Category
        subCategory.color = subCategoryColors[index]
        categories.push(subCategory)
      })
    })

    return categories
  }

  public getCategoryName(categoryId: number): string {
    return this.categoryNamesById[categoryId]
  }

  private flattenCategories(): FlatCategory[] {
    let flatCategories = this.categories.map(c => {
      const flatCategory = c as FlatCategory
      if (c.parentId === undefined) {
        flatCategory.fullName = flatCategory.name
        return flatCategory
      }

      let topLevelCategory = this.categories.find(sc => sc.id === c.parentId)
      if (topLevelCategory === undefined) throw new Error("Top level category for subcategory cannot be found")
      flatCategory.fullName = topLevelCategory.name === c.name ? c.name : `${topLevelCategory.name} - ${c.name}`
      flatCategory.topLevelCategory = topLevelCategory
      return flatCategory
    })
    flatCategories.sort((c1, c2) => c1.fullName.localeCompare(c2.fullName))
    return flatCategories
  }

  private getCategoryNamesById() {
    let categoriesById: Record<number, string> = {}
    this.flatSubCategories.forEach((category) => {
      categoriesById[category.id] = category.fullName
    })
    return categoriesById
  }

  private getCategoriesById() {
    let categoriesById: Record<number, Category> = {}
    this.categoriesIncludingTopLevel.forEach((category) => {
      categoriesById[category.id] = category
    })
    return categoriesById
  }

  private addTopLevelCategoryNamesById() {
    this.categories.filter(c => c.parentId == null).forEach((category) => {
      this.categoryNamesById[category.id] = category.name
    })
  }

  private createCategoryTree() {
    return this.categories
      .filter(c => c.parentId == null)
      .map(topLevelCategory => {
        const treeCategory = topLevelCategory as TreeCategory
        treeCategory.subCategories = this.categories.filter(c => c.parentId === topLevelCategory.id)
        return treeCategory
      })
  }

  private getMainColorById(): Record<number, string> {
    const colorById: Record<number, string> = {}
    this.categoriesIncludingTopLevel.forEach(category => {
      colorById[category.id] = getColorForTopLevelCategory(category.parentId === undefined ? category.id : category.parentId)
    })
    return colorById
  }
}